import { useDispatch, useSelector } from 'react-redux';
import { ISpatStorageResource } from '../../entities/farms.entities';
import { selectLang } from '../../store/ui/ui.selector';
import { useState } from 'react';
import { sendSingleRequest } from '../../apis';
import { loadSpatStorages } from '../../store/farms/farms.actions';
import { translate } from '../../lib/lang.helper';
import { Button, Modal } from 'antd';
import {
  CloseIcon,
  Input,
  Paragrapgh,
  Subtitle,
} from '../../components/shared';
import { displayValueUnit, numToStr } from '../../lib/common.helpers';
import { selectAccount } from '../../store/auth/auth.selector';

const weightLabel = (spat: ISpatStorageResource, unitType: any) =>
  spat.available_weight === null
    ? '-'
    : displayValueUnit(
        spat.available_weight,
        unitType,
        spat.type === 'OYSTER' ? 'pcs' : 'weight_kg',
      );

interface Props {
  spatStorage: ISpatStorageResource;
  onClose: () => void;
}

const SpatArchiveModal = ({ spatStorage, onClose }: Props) => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const unitType = useSelector(selectAccount)?.display_unit;

  const [disabled, setDisabled] = useState(false);
  const [misPercent, setMisPercent] = useState<number | undefined>(
    !spatStorage.available_weight || spatStorage.available_weight <= 0
      ? 0
      : undefined,
  );

  const changeMisPercent = (val: string, type: 'mis' | 'mor') => {
    if (val.length <= 0) {
      setMisPercent(undefined);
    } else {
      if (type === 'mis') setMisPercent(Math.min(100, Number(val)));
      else setMisPercent(Math.max(0, 100 - Number(val)));
    }
  };
  const archiveClick = async (spatStatus: 'ARCHIVED' | 'SOLD') => {
    if (spatStatus !== 'SOLD' && misPercent === undefined) {
      window.alert('Please enter percent of miscalculation and mortality');
      return;
    }
    const r = window.confirm('Are you sure you want to proceed?');
    if (!r) return;

    const available = spatStorage.available_weight ?? 0;
    const mortality =
      spatStatus === 'SOLD'
        ? undefined
        : Math.round(available * (1 - misPercent! / 100));
    setDisabled(true);
    const res = await sendSingleRequest(
      {
        close_action: true,
        status: spatStatus,
        loss: mortality,
      },
      'PUT',
      `api/farm/spat-storage/${spatStorage.id}`,
      true,
    );
    if (res.status) {
      await dispatch(loadSpatStorages());
      onClose();
    } else {
      alert(translate(lang, res.data?.message ?? 'Server error'));
      setDisabled(false);
    }
  };
  const deleteClick = async () => {
    const r = window.confirm(
      'Are you sure you want to delete this spat from history? This action cannot be undone.',
    );
    if (!r) return;
    setDisabled(true);
    const res = await sendSingleRequest(
      {},
      'DELETE',
      `api/farm/spat-storage/${spatStorage.id}`,
      true,
    );
    if (res.status) {
      await dispatch(loadSpatStorages());
      onClose();
    } else {
      alert(translate(lang, res.data?.message ?? 'Server error'));
      setDisabled(false);
    }
  };

  return (
    <Modal
      visible={true}
      footer={null}
      onCancel={onClose}
      closable={true}
      closeIcon={<CloseIcon />}
      width={600}
    >
      <div className='wrap'>
        <div className='mt-28'>
          {!spatStorage.available_weight ||
          spatStorage.available_weight <= 0 ? (
            <Subtitle size={4} color='black-1' align='center' fontWeight={600}>
              {'Are you sure to archive this spat storage permanently?'}
            </Subtitle>
          ) : (
            <Subtitle size={4} color='black-1' align='left' fontWeight={600}>
              {`You are about to archive this spat storage, even though it still has ${weightLabel(
                spatStorage,
                unitType,
              )} available. What would you like to do?`}
            </Subtitle>
          )}
        </div>
        <div className='mt-18 pl-18 pr-18'>
          {spatStorage.seedings.length > 0 && (
            <Paragrapgh size={2} color='black' fontWeight={400} align='center'>
              {`The spat is still hanging on lines: ${spatStorage.seedings
                .map(x => `${x.line?.farm.name} ${x.line?.line_name}`)
                .join(', ')}`}
            </Paragrapgh>
          )}
          {!!spatStorage.available_weight &&
            spatStorage.available_weight >= spatStorage.weight && (
              <Paragrapgh
                size={2}
                color='black'
                fontWeight={400}
                align='center'
              >
                {!spatStorage.source_line
                  ? translate(lang, '_spat_fully_harvested')
                  : translate(
                      lang,
                      '_spat_came_from',
                      `${spatStorage.source_line.farm.name} - ${spatStorage.source_line.line_name}`,
                    )}
              </Paragrapgh>
            )}
        </div>
        {!!spatStorage.available_weight && spatStorage.available_weight > 0 && (
          <>
            <div className='mt-32'>
              <Subtitle
                size={4}
                color='black-1'
                align='center'
                fontWeight={600}
              >
                {translate(lang, '_spat_diff_mortality')}
              </Subtitle>
            </div>
            <div className='mt-24 mb-24 d-flex'>
              <div className='mr-7'>
                <Input
                  type='number'
                  label={translate(lang, '_percent_miscalculation')}
                  value={numToStr(misPercent)}
                  onChange={e => changeMisPercent(e.target.value, 'mis')}
                  unit='%'
                />
              </div>
              <div className='ml-7'>
                <Input
                  type='number'
                  label={translate(lang, 'Percent of mortality')}
                  value={
                    misPercent !== undefined
                      ? (100 - misPercent).toString()
                      : ''
                  }
                  onChange={e => changeMisPercent(e.target.value, 'mor')}
                  unit='%'
                />
              </div>
            </div>
          </>
        )}
        <div className='mt-18 d-flex justify-center pr-18 pl-18 mb-18'>
          <div className='mr-18'>
            <Button
              type='primary'
              onClick={() => archiveClick('ARCHIVED')}
              disabled={disabled}
            >
              {translate(lang, 'Archive')}
            </Button>
          </div>
          <div className='ml-18'>
            <Button
              type='default'
              danger={true}
              onClick={onClose}
              disabled={disabled}
            >
              {translate(lang, 'Cancel')}
            </Button>
          </div>
        </div>
        <div className='mt-64 mb-32 d-flex justify-center'>
          <div className='mr-18'>
            <Button
              type='primary'
              onClick={() => archiveClick('SOLD')}
              disabled={disabled}
            >
              {translate(lang, 'Spat moved to another account')}
            </Button>
          </div>
          <div className='ml-18'>
            <Button
              type='primary'
              danger={true}
              onClick={deleteClick}
              disabled={disabled}
            >
              {translate(lang, 'Delete spat from history')}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default SpatArchiveModal;
